import { Overlay } from '@eti/components';
import { type ReactNode, type MouseEvent, type Dispatch, type SetStateAction } from 'react';
import type { TripType } from '@eti/schema-types';
import { css } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { Stack } from '@etg/wings';
import TripItinerary from './TripItinerary';
import type { Bound, UpgradeTypePerBound } from './models';

const headerStyles = css`
  position: relative;

  & > button {
    inset-inline-start: 8px;
    position: absolute;
  }
`;

export const getJourneyType = (tripType: TripType) => {
  switch (tripType) {
    case 'ONE_WAY':
      return 'Result.TripDetails.OneWay.Title';
    case 'RETURN':
      return 'Result.TripDetails.ReturnTrip.Title';
    default:
      return 'Result.TripDetails.MultiStopTrip.Title';
  }
};

interface TripItineraryDrawerProps {
  afterItineraryContent?: ReactNode;
  backgroundColor?: string;
  beforeItineraryContent?: ReactNode;
  bounds: Bound[];
  footer?: ReactNode;
  hasBoundBorder?: boolean;
  id?: string;
  isOpen: boolean;
  onClose: (event: MouseEvent<HTMLButtonElement>) => void;
  setIsItineraryDrawerSelfTransferModalOpen?: Dispatch<SetStateAction<boolean>>;
  selectedBoundId?: string;
  shouldHaveHeaderWithBackButton?: boolean;
  shouldShowTripItinerary?: boolean;
  title: string;
  tripType: TripType;
  segmentsCabinUpgradePrices?: UpgradeTypePerBound[] | null;
}

const TripItineraryDrawer = ({
  afterItineraryContent,
  backgroundColor,
  beforeItineraryContent,
  bounds,
  footer,
  hasBoundBorder,
  id,
  isOpen,
  onClose,
  setIsItineraryDrawerSelfTransferModalOpen,
  selectedBoundId,
  shouldHaveHeaderWithBackButton,
  shouldShowTripItinerary = true,
  title,
  tripType,
  segmentsCabinUpgradePrices,
}: TripItineraryDrawerProps) => {
  const { t } = useTranslation();

  return (
    <Overlay.Drawer
      backgroundColor={backgroundColor}
      dataTestId="itinerary-drawer"
      handleClose={onClose}
      id={id}
      isOpen={isOpen}
    >
      {shouldHaveHeaderWithBackButton ? (
        <Overlay.HeaderWithBackButton
          className={headerStyles}
          journeyType={t(getJourneyType(tripType))}
          onClose={onClose}
          title={title}
        />
      ) : (
        <Overlay.Header onClose={onClose} title={title} />
      )}

      <Overlay.Content data-testid="itinerary-drawer-content">
        <Stack>
          {beforeItineraryContent}
          {shouldShowTripItinerary && (
            <TripItinerary
              bounds={bounds}
              hasBoundBorder={hasBoundBorder}
              segmentsCabinUpgradePrices={segmentsCabinUpgradePrices}
              selectedBoundId={selectedBoundId}
              setIsItineraryDrawerSelfTransferModalOpen={setIsItineraryDrawerSelfTransferModalOpen}
              tripType={tripType}
            />
          )}
          {afterItineraryContent}
        </Stack>
      </Overlay.Content>
      {footer && <Overlay.Footer data-testid="itinerary-drawer-footer">{footer}</Overlay.Footer>}
    </Overlay.Drawer>
  );
};

export default TripItineraryDrawer;
